import type { SharedHomeBillKey } from '../home';

type ManualFinanceSetupTenure = string | null | undefined;

export type ManualFinanceSetupSeedBill = {
  billKey: SharedHomeBillKey;
  amount: number | null;
  providerName: string | null;
};

export type ManualFinanceSetupSeed = {
  annualIncome?: number | null;
  tenure?: ManualFinanceSetupTenure;
  monthlyRent?: number | null;
  landlordName?: string | null;
  mortgageProvider?: string | null;
  utilityGasProvider?: string | null;
  utilityElectricProvider?: string | null;
  utilityBroadbandProvider?: string | null;
  utilityBroadbandContractEnd?: string | null;
  utilityPhoneProvider?: string | null;
  bills?: ManualFinanceSetupSeedBill[];
};

export type ManualFinanceSetupDraft = {
  monthlyIncome: string;
  housingMonthlyCost: string;
  housingProvider: string;
  councilTaxMonthly: string;
  energyProvider: string;
  gasMonthly: string;
  electricityMonthly: string;
  waterMonthly: string;
  broadbandMonthly: string;
  broadbandProvider: string;
  broadbandContractEnd: string;
  mobilePhoneMonthly: string;
  phoneProvider: string;
};

export type ManualFinanceSetupBillRow = {
  user_id: string;
  bill_key: SharedHomeBillKey;
  enabled: boolean;
  frequency: 'monthly';
  amount: number | null;
  due_day_of_week: null;
  due_day_of_month: null;
  annual_due_date: null;
  provider_name: string | null;
  provider_account: null;
};

export type ManualFinanceSetupHomeProfilePatch = {
  user_id: string;
  monthly_rent?: number | null;
  landlord_name?: string | null;
  mortgage_provider?: string | null;
  utility_gas_provider?: string | null;
  utility_electric_provider?: string | null;
  utility_broadband_provider?: string | null;
  utility_broadband_contract_end?: string | null;
  utility_phone_provider?: string | null;
};

export const MANUAL_FINANCE_SETUP_TITLE = 'Finance basics';
export const MANUAL_FINANCE_SETUP_BODY =
  'Add the core money picture once so Home, Money, Work, and the saved setup summaries start from the same baseline.';
export const MANUAL_FINANCE_SETUP_NOTE =
  'Use the household totals you already know. If benefits or tax-free help already form part of the money coming in, include them in the total and refine the detail later.';
export const MANUAL_FINANCE_SETUP_SUCCESS =
  'Finance basics saved. Adulting can now reuse the same starting picture across Money, Home, and setup.';

function formatSetupAmount(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '';
  const rounded = Math.round(value * 100) / 100;
  const fixed = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  return fixed.replace(/\.00$/, '').replace(/(\.\d*[1-9])0$/, '$1');
}

function parseSetupAmount(value: string) {
  const trimmed = value.trim().replace(/,/g, '');
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function getSeedBill(seed: ManualFinanceSetupSeed, billKey: SharedHomeBillKey) {
  return seed.bills?.find((bill) => bill.billKey === billKey) || null;
}

export function resolveManualFinanceHousingBillKey(tenure: ManualFinanceSetupTenure): SharedHomeBillKey | null {
  if (!tenure) return null;
  return tenure === 'owner-occupier' ? 'mortgage' : 'rent';
}

export function getManualFinanceHousingCostLabel(tenure: ManualFinanceSetupTenure) {
  return resolveManualFinanceHousingBillKey(tenure) === 'mortgage' ? 'Monthly mortgage (£)' : 'Monthly rent / housing cost (£)';
}

export function getManualFinanceHousingProviderLabel(tenure: ManualFinanceSetupTenure) {
  return resolveManualFinanceHousingBillKey(tenure) === 'mortgage' ? 'Mortgage provider' : 'Landlord or housing provider';
}

export function buildManualFinanceSetupDraft(seed: ManualFinanceSetupSeed): ManualFinanceSetupDraft {
  const housingBillKey = resolveManualFinanceHousingBillKey(seed.tenure);
  const housingBill = housingBillKey ? getSeedBill(seed, housingBillKey) : null;
  const councilTaxBill = getSeedBill(seed, 'council-tax');
  const gasBill = getSeedBill(seed, 'gas');
  const electricityBill = getSeedBill(seed, 'electricity');
  const waterBill = getSeedBill(seed, 'water');
  const broadbandBill = getSeedBill(seed, 'broadband');
  const mobileBill = getSeedBill(seed, 'mobile-phone');
  const monthlyIncome = seed.annualIncome && seed.annualIncome > 0 ? seed.annualIncome / 12 : null;

  return {
    monthlyIncome: formatSetupAmount(monthlyIncome),
    housingMonthlyCost: formatSetupAmount(housingBill?.amount ?? (housingBillKey === 'rent' ? seed.monthlyRent : null)),
    housingProvider: (housingBill?.providerName
      || (housingBillKey === 'mortgage' ? seed.mortgageProvider : seed.landlordName)
      || ''
    ).trim(),
    councilTaxMonthly: formatSetupAmount(councilTaxBill?.amount),
    energyProvider: (seed.utilityElectricProvider || seed.utilityGasProvider || '').trim(),
    gasMonthly: formatSetupAmount(gasBill?.amount),
    electricityMonthly: formatSetupAmount(electricityBill?.amount),
    waterMonthly: formatSetupAmount(waterBill?.amount),
    broadbandMonthly: formatSetupAmount(broadbandBill?.amount),
    broadbandProvider: (broadbandBill?.providerName || seed.utilityBroadbandProvider || '').trim(),
    broadbandContractEnd: (seed.utilityBroadbandContractEnd || '').trim(),
    mobilePhoneMonthly: formatSetupAmount(mobileBill?.amount),
    phoneProvider: (mobileBill?.providerName || seed.utilityPhoneProvider || '').trim(),
  };
}

export function hasManualFinanceSetupInput(draft: ManualFinanceSetupDraft) {
  return Object.values(draft).some((value) => value.trim().length > 0);
}

export function buildManualFinanceSetupAnnualIncome(draft: ManualFinanceSetupDraft) {
  const monthlyIncome = parseSetupAmount(draft.monthlyIncome);
  return monthlyIncome == null ? null : Math.round(monthlyIncome * 12 * 100) / 100;
}

export function buildManualFinanceSetupBillRows(userId: string, tenure: ManualFinanceSetupTenure, draft: ManualFinanceSetupDraft): ManualFinanceSetupBillRow[] {
  const rows: ManualFinanceSetupBillRow[] = [];
  const housingBillKey = resolveManualFinanceHousingBillKey(tenure);
  const energyProvider = draft.energyProvider.trim() || null;

  const maybePush = (billKey: SharedHomeBillKey, amountValue: string, providerName?: string | null) => {
    const amount = parseSetupAmount(amountValue);
    const provider = providerName?.trim() || null;
    if (amount == null && !provider) return;

    rows.push({
      user_id: userId,
      bill_key: billKey,
      enabled: true,
      frequency: 'monthly',
      amount,
      due_day_of_week: null,
      due_day_of_month: null,
      annual_due_date: null,
      provider_name: provider,
      provider_account: null,
    });
  };

  if (housingBillKey) {
    maybePush(housingBillKey, draft.housingMonthlyCost, draft.housingProvider);
  }

  maybePush('council-tax', draft.councilTaxMonthly);
  maybePush('gas', draft.gasMonthly, energyProvider);
  maybePush('electricity', draft.electricityMonthly, energyProvider);
  maybePush('water', draft.waterMonthly);
  maybePush('broadband', draft.broadbandMonthly, draft.broadbandProvider);
  maybePush('mobile-phone', draft.mobilePhoneMonthly, draft.phoneProvider);

  return rows;
}

export function buildManualFinanceSetupHomeProfilePatch(
  userId: string,
  tenure: ManualFinanceSetupTenure,
  draft: ManualFinanceSetupDraft,
): ManualFinanceSetupHomeProfilePatch {
  const housingBillKey = resolveManualFinanceHousingBillKey(tenure);
  const housingCost = parseSetupAmount(draft.housingMonthlyCost);
  const energyProvider = draft.energyProvider.trim() || null;
  const broadbandProvider = draft.broadbandProvider.trim() || null;
  const phoneProvider = draft.phoneProvider.trim() || null;

  const patch: ManualFinanceSetupHomeProfilePatch = {
    user_id: userId,
  };

  if (housingBillKey === 'rent') {
    patch.monthly_rent = housingCost;
    patch.landlord_name = draft.housingProvider.trim() || null;
  } else if (housingBillKey === 'mortgage') {
    patch.mortgage_provider = draft.housingProvider.trim() || null;
  }

  if (energyProvider) {
    patch.utility_gas_provider = energyProvider;
    patch.utility_electric_provider = energyProvider;
  }

  if (broadbandProvider) {
    patch.utility_broadband_provider = broadbandProvider;
  }
  if (draft.broadbandContractEnd.trim()) {
    patch.utility_broadband_contract_end = draft.broadbandContractEnd.trim();
  }
  if (phoneProvider) {
    patch.utility_phone_provider = phoneProvider;
  }

  return patch;
}
